import { useLocale } from '@solinkcorp/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  cn,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@solinkcorp/solink-ui';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  formatTimeForDisplay,
  staggeredDayPartLeftPercent,
  staggeredDayPartWidthPercent,
  type DayIndex,
  type DayPartOverlapLayout,
} from '../../day-part/utils';

interface DayPartCardProps {
  id: string;
  label: string;
  dayKey: DayIndex;
  start: string;
  end: string;
  overlapLayout: DayPartOverlapLayout;
  onEdit: () => void;
  onDelete: (id: string, dayKey: DayIndex) => void;
}

/** Parse "HH:MM" → total minutes from midnight */
const parseTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const DayPartCard = (props: DayPartCardProps) => {
  const { id, label, dayKey, start, end, onEdit, onDelete, overlapLayout } = props;
  const locale = useLocale();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(end);
  const topPx = startMin;
  const heightPx = endMin - startMin;
  const confirmLocale = locale.serviceDisplayMonitor.dayPartDialog;

  const isStaggered = overlapLayout.maxConcurrent > 1;
  const leftPercent = isStaggered
    ? staggeredDayPartLeftPercent(overlapLayout.columnIndex, overlapLayout.maxConcurrent)
    : undefined;
  const widthPercent = isStaggered
    ? staggeredDayPartWidthPercent(overlapLayout.maxConcurrent)
    : undefined;
  const staggerStyle = isStaggered
    ? { left: `${leftPercent}%`, width: `${widthPercent}%`, right: 'auto' as const }
    : undefined;

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverAnchor asChild>
          <div
            role="button"
            tabIndex={0}
            aria-expanded={menuOpen}
            aria-haspopup="dialog"
            className={cn(
              'absolute left-px right-px z-[5] mr-px cursor-pointer overflow-hidden rounded-md border border-background-surface bg-foreground-accent p-2 pr-0 transition-[background-color] duration-150 hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
              menuOpen && 'bg-primary-700',
            )}
            style={{
              top: `${topPx + 1}px`,
              height: `${heightPx - 1}px`,
              ...staggerStyle,
            }}
            onClick={() => setMenuOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setMenuOpen(true);
              }
            }}
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1 overflow-hidden">
                <span className="block whitespace-nowrap font-title text-sm font-semibold leading-snug tracking-tight text-interaction-primary-content-default">
                  {label}
                </span>
                <span className="block whitespace-nowrap font-body text-xs font-normal leading-snug tracking-normal text-interaction-primary-content-default">
                  {formatTimeForDisplay(start)} – {formatTimeForDisplay(end)}
                </span>
              </div>
            </div>
          </div>
        </PopoverAnchor>
        <PopoverContent
          side="left"
          align="start"
          sideOffset={8}
          className="min-w-[200px] max-w-[min(280px,calc(100vw-24px))] bg-background-surface p-0"
        >
          <div className="p-3">
            <div className="space-y-1 pr-1">
              <p className="break-words font-title text-sm font-semibold leading-snug tracking-tight text-text-primary">
                {label}
              </p>
              <p className="font-body text-xs font-normal leading-snug tracking-normal text-text-secondary">
                {formatTimeForDisplay(start)} – {formatTimeForDisplay(end)}
              </p>
            </div>
            <div className="mt-3 flex justify-end gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={locale.general.delete}
                className="shrink-0 gap-0 rounded-md p-0 hover:text-red-500"
                onClick={() => {
                  setMenuOpen(false);
                  setShowDeleteConfirm(true);
                }}
              >
                <Trash2 className="size-4 shrink-0" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={locale.general.edit}
                className="shrink-0 gap-0 rounded-md p-0"
                onClick={() => {
                  setMenuOpen(false);
                  onEdit();
                }}
              >
                <Pencil className="size-4 shrink-0" />
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="sm:!max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="w-max">
              {confirmLocale.confirmDeleteTitle.replace('{__value__}', label)}
            </AlertDialogTitle>
          </AlertDialogHeader>
          {/*
            OUTLIER pattern — do not copy for new dialogs. AlertDialogDescription is used
            as the body message here: it is moved out of the header and restyled from the
            default subheading look (`text-foreground-secondary body-sm`) to body text
            (`text-foreground-primary body-md`). It only reads correctly because of that
            extra styling. Prefer a title-only header + a body node + an `sr-only`
            AlertDialogDescription for the accessible name.
          */}
          <AlertDialogDescription className="text-foreground-primary body-md">
            {confirmLocale.confirmDeleteDescription}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel variant="outline" size="default">
              {locale.general.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              size="default"
              onClick={() => onDelete(id, dayKey)}
            >
              {locale.general.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
